import { useQueryClient, useQuery, skipToken } from '@tanstack/react-query';
import { CustomPayment } from '@apply/api/src/api/PaymentApi';
import { customPaymentQueryKey } from '../keys/customPaymentQueryKey';

/**
 * Reads and writes a single field on a single Custom Payment record in the React Query cache.
 * Supports creating a new Custom Payment when no customPaymentId is provided.
 */
export function useCustomPaymentField<T>(
    paymentGuid: string,
    selector: (customPayment: CustomPayment) => T,
    updater: (customPayment: CustomPayment, value: T) => CustomPayment
): [T | undefined, (value: T) => void] {
    const queryClient = useQueryClient();

    const { data: currentValue } = useQuery<CustomPayment | undefined, Error, T | undefined>({
        queryKey: customPaymentQueryKey(paymentGuid),
        queryFn: skipToken,
        select: (exp) => {
            try {
                return selector(exp as CustomPayment);
            } catch {
                return undefined;
            }
        },
    });

    const setValue = (value: T) => {
        queryClient.setQueryData<CustomPayment | undefined>(customPaymentQueryKey(paymentGuid), (old) => {
            // If no existing record, hydrate a SAFE fully-shaped object
            if (!old) {
                const hydrated: CustomPayment = {
                    paymentGuid,
                    billingDetails: {
                        name: undefined,
                        phone: undefined,
                        email: undefined,
                        address: {
                            city: undefined,
                            country: undefined,
                            line1: undefined,
                            line2: undefined,
                            postal_code: undefined,
                            state: undefined,
                        },
                    },
                    isPropertyInArizona: undefined,
                    paymentPurpose: undefined,
                    propertyLoanReference: undefined,
                    amount: undefined,
                };

                return updater(hydrated, value);
            }

            // Otherwise update the existing record
            return updater(old, value);
        });
    };

    return [currentValue, setValue];
}
